import type Phaser from 'phaser';

// Bundled music tracks (key = filename, e.g. `C1-E1_E2`). The glob only turns
// each file into a hashed URL — nothing is downloaded until a loader asks.
const MUSIC_FILES = import.meta.glob('/assets/music/*.mp3', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

/** Track key → bundled URL. */
export const MUSIC_URLS: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(MUSIC_FILES).map(([path, url]) => [
    path
      .split('/')
      .pop()!
      .replace(/\.mp3$/, ''),
    url,
  ]),
);

/**
 * The menu theme — the only track BootScene loads up front, because it plays
 * the moment the title screen appears. Every other track (each is 3–6 MB) is
 * fetched the first time it is asked for; see `MusicService.playTrack`.
 */
export const MENU_TRACK = 'episode-selection';

/** Fetches a track into the audio cache and calls `onReady` once it is there. */
export type TrackFetcher = (key: string, onReady: () => void) => void;

/**
 * The game's TrackFetcher: queues the file on a scene's loader. It prefers
 * EffectTimerScene, which is launched at boot and never stops, so a download
 * is not cut off when the scene that asked for it shuts down; any active
 * scene serves otherwise (the `?debug` gallery never launches it). Requests
 * for a key already in flight share one download. A failed download is
 * forgotten, so the next request tries again.
 */
export function sceneTrackFetcher(game: Phaser.Game): TrackFetcher {
  const waiting = new Map<string, (() => void)[]>();
  return (key, onReady) => {
    const url = MUSIC_URLS[key];
    if (!url) return;
    const queued = waiting.get(key);
    if (queued) {
      queued.push(onReady);
      return;
    }
    const host = game.scene.isActive('EffectTimerScene')
      ? game.scene.getScene('EffectTimerScene')
      : game.scene.getScenes(true)[0];
    if (!host) return;
    waiting.set(key, [onReady]);
    const load = host.load;
    const done = () => {
      load.off('loaderror', failed);
      const cbs = waiting.get(key) ?? [];
      waiting.delete(key);
      if (game.cache.audio.exists(key)) cbs.forEach((cb) => cb());
    };
    const failed = (file: Phaser.Loader.File) => {
      if (file.key !== key) return;
      load.off(`filecomplete-audio-${key}`, done);
      load.off('loaderror', failed);
      waiting.delete(key);
    };
    load.once(`filecomplete-audio-${key}`, done);
    load.on('loaderror', failed);
    load.audio(key, url);
    load.start();
  };
}
